import { AssetFormFields } from '@Types/Form.types';
import { DROPDOWN_DEFAULT } from '@Utils/constants';
import { isValidEmail } from './email.utils';
import { isAssetDescriptionRequired, isSerialNumberRequired } from './form.utils';

export const VALIDATION_RULES = {
  TAX_STATUS_RULES: {
    required: 'Description is required',
    maxLength: { value: 50, message: 'Description cannot be longer than 50 characters.' },
  },
  ACCESSORY_RULES: {
    required: 'Name is required',
    maxLength: { value: 50, message: 'Name cannot be longer than 50 characters.' },
  },
  ASSET_STATUS_RULES: {
    required: 'Description is required',
    maxLength: { value: 50, message: 'Description cannot be longer than 50 characters.' },
  },
  ASSET_TYPE_RULES: {
    CODE: {
      required: 'Code is required',
      maxLength: { value: 25, message: 'Code cannot be longer than 25 characters.' },
    },
    DESCRIPTION: {
      required: 'Description is required',
      maxLength: { value: 50, message: 'Description cannot be longer than 50 characters.' },
    },
  },
  LOCATION_RULES: {
    NAME: {
      required: 'Name is required.',
      maxLength: { value: 25, message: 'Name cannot be longer than 25 characters.' },
    },
    LOCATION: {
      required: 'Location is required.',
      maxLength: { value: 50, message: 'Location cannot be longer than 50 characters.' },
    },
    COUNTRY: {
      validate: {
        required: (value: string) => {
          if (!value || value === DROPDOWN_DEFAULT.value) {
            return 'Country is required';
          }
          return undefined;
        },
      },
    },
    DETAILS: {
      maxLength: { value: 100, message: 'Details cannot be longer than 100 characters.' },
    },
  },
  ADMIN_RULES: {
    NAME: {
      validate: {
        required: (value: string) => {
          if (!value || value === DROPDOWN_DEFAULT.value) {
            return 'Name is required';
          }
          return undefined;
        },
      },
    },
    ROLE: {
      validate: {
        required: (value: string) => {
          if (!value || value === DROPDOWN_DEFAULT.value) {
            return 'Role is required';
          }
          return undefined;
        },
      },
    },
  },
  DEVICE_INFO_RULES: {
    MAKE: {
      required: 'Brand is required',
      maxLength: { value: 50, message: 'Make cannot be longer than 50 characters.' },
    },
    MODEL: {
      maxLength: { value: 255, message: 'Model cannot be longer than 255 characters.' },
    },
    YEAR: {
      min: { value: 1900, message: 'The min value is 1900.' },
      max: {
        value: new Date().getFullYear() + 1,
        message: `The max value is ${new Date().getFullYear() + 1}`,
      },
      pattern: {
        value: /^(19|2[0-9])\d{2}$/,
        message: `Must be a valid Year (1900 - ${new Date().getFullYear() + 1})`,
      },
    },
    YEAR_INPUT: { inputMode: 'numeric', maxLength: 4 },
    ASSET_TYPE: {
      validate: {
        required: (value: string) => {
          if (!value || value === DROPDOWN_DEFAULT.value) {
            return 'Asset Type is required';
          }
          return undefined;
        },
      },
      maxLength: { value: 24, message: 'Status cannot be longer than 24 characters.' },
    },
    ASSET_STATUS: {
      validate: {
        required: (value: string) => {
          if (!value || value === DROPDOWN_DEFAULT.value) {
            return 'Status is required';
          }
          return undefined;
        },
      },
      maxLength: { value: 24, message: 'Status cannot be longer than 24 characters.' },
    },
    DESCRIPTION: {
      validate: {
        required: async (value: string, data: AssetFormFields) => {
          const required = await isAssetDescriptionRequired(data.assetType);
          if (required && !value?.trim()) {
            return 'Asset Description is required for the selected asset type';
          }
          return true;
        },
      },
      maxLength: { value: 255, message: 'Description cannot be longer than 255 characters.' },
    },
    OS_VERSION: {
      maxLength: { value: 50, message: 'Chip cannot be longer than 50 characters.' },
    },
    STORAGE: {
      maxLength: { value: 255, message: 'Storage cannot be longer than 255 characters.' },
    },
  },
  EMAIL_RULES: {
    SUBJECT: {
      required: 'Subject is required.',
    },
    BODY: {
      required: 'Body is required.',
    },
    EMAIL: {
      required: 'Email is required.',
      validate: (value: string) => isValidEmail(value) || 'Must be a valid Konrad Group or BrainStation email',
    },
  },
  LOCATION_INFO_RULES: {
    OFFICE: {
      validate: {
        required: (value: string) => {
          if (!value || value === DROPDOWN_DEFAULT.value) {
            return 'Office is required';
          }
          return undefined;
        },
      },
      maxLength: { value: 24, message: 'Office cannot be longer than 50 characters.' },
    },
    DETAILS: {
      maxLength: { value: 50, message: 'Office Details cannot be longer than 50 characters.' },
    },
  },
  LEGAL_INFO_RULES: {
    SERIAL_NUMBER_ALT: {
      validate: {
        required: async (value: string, data: AssetFormFields) => {
          const required = await isSerialNumberRequired(data.assetType);
          if (required && !value?.trim()) {
            return 'Serial Number is required for the selected asset type';
          }
          return true;
        },
      },
      maxLength: { value: 50, message: 'Serial Number  cannot be longer than 50 characters.' },
    },
    ASSET_NUMBER: {
      maxLength: { value: 7, message: 'Asset Number cannot be longer than 7 characters.' },
      placeholder: 'Type Something...',
    },
    RFID: {
      maxLength: { value: 24, message: 'RFID cannot be longer than 24 characters.' },
    },
    TAX_STATUS: {
      validate: {
        required: (value: string) => {
          if (!value || value === DROPDOWN_DEFAULT.value) {
            return 'Tax Status is required';
          }
          return undefined;
        },
      },
    },
    VENDOR: {
      maxLength: { value: 50, message: 'Vendor cannot be longer than 50 characters.' },
    },
    PURCHASE_DATE: {
      valueAsDate: true,
    },
    WARRANTY: {
      valueAsNumber: 'This value must be a number.',
      months: 'Type the warranty in months.',
    },
    INVOICE_NUMBER: {
      maxLength: 255,
    },
  },
  ASSIGN_ASSETS_RULES: {
    OWNER: {
      required: 'Valid Owner is required',
      validate: (value: string) => isValidEmail(value) || 'Must be a valid Konrad Group or BrainStation email',
    },
    DESCRIPTION: {
      required: 'Description is required',
    },
    ASSETS: {
      required: 'Must select either an asset or an accessory to assign',
    },
  },
  SEARCH_BAR_RULES: {
    max: new Date().getFullYear(),
  },
  REPORTS: {
    NEW_REPORT: {
      FILE_NAME: {
        required: 'Filename is required',
      },
      REPORT_DATA: {
        required: true,
      },
    },
  },
  UNASSIGN_OWNER: {
    REASON: {
      required: 'Please provide a reason for this removal',
      maxLength: { value: 150, message: 'Reason cannot be longer than 150 characters.' },
    },
  },
  ASSIGN_OWNER: {
    OWNER: {
      validate: {
        required: (value: string) => {
          if (!value || value === DROPDOWN_DEFAULT.value) {
            return 'Owner is required';
          }
          return undefined;
        },
      },
    },
    NOTES: {
      required: 'Notes are required',
    },
  },
  ACCESSORY: {
    NAME: {
      required: 'Name is required',
      maxLength: { value: 50, message: 'Name cannot be longer than 50 characters.' },
    },
  },
  TAX_STATUS: {
    DESCRIPTION: {
      required: 'Description is required',
      maxLength: { value: 50, message: 'Description cannot be longer than 50 characters.' },
    },
  },
  ASSET_STATUS: {
    DESCRIPTION: {
      required: 'Name is required',
      maxLength: { value: 50, message: 'Name cannot be longer than 50 characters.' },
    },
  },
  TEMPLATE_NAME: {
    required: 'Template name is required',
  },
};
